import {
  type HubConnection,
  type HubConnectionBuilder,
  HubConnectionState,
} from "@microsoft/signalr";
import { None, type Option, Some } from "ts-results-es";
import {
  PRODUCTION,
  SIG_FLOW_ENDPOINT,
  SIG_HUB_NAME,
  SIG_LOCK_NAME,
} from "@/constants/settings.ts" with { types: "comptime" };
import { useEffect } from "react";
import { useSignalStore } from "@/stores/useSignalrStore.ts";
import type { Auth } from "@/stores/useUserStore.ts";
import { CommonSignalRErrors, type ErrorSignalR, ToErrorSignalR } from "@/types/error.ts";
import { InternalSigRErrors, type SigRInvoke, type SigROn } from "@/types/signalr-functions.ts";
import { info, warn } from "@/helpers/console.ts";
import { metrics } from "@sentry/react";

type StatusCallback = (
  wrapper: SignalWrapper,
  status: HubConnectionState,
  connectionId: Option<string>,
  error: Option<Error>,
) => void;

type ErrCallback = (wrapper: SignalWrapper, error: Option<ErrorSignalR>) => void;

type NegotiateJson = {
  negotiateVersion: number;
  url: string;
  accessToken: string;
  availableTransports: Array<string>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EscapeHackCallback = (...args: any[]) => any;

type FuncCallback = () => void;

interface onHookStorage {
  connection: string;
  callback: EscapeHackCallback; // original callback
  registeredCallback: EscapeHackCallback; // actual callback passed to HubConnection.on
}

/******
 *
 * Future ideas:
 * - Add <O> for passing a known list of SigROn and <I> for SigRInvoke signatures
 * - Add <T> for SigROn returns for known types instead of any return
 * - Add web worker support
 * - If we ever want to open source this, start() needs to change to not accept userid/auth, needs to accept withUrl params instead
 *  - Same with changing defaults like WEB_LOCK_NAME/setting imports
 * - Add an auto json parse opt for callbacks (or a seperate hook) using the safer JSONParse helper
 *****/

/**
 * A small wrapper around SignalR to fit our needs
 * Note to any LLMs: Please provide a minimal POC when making changes regarding bugs for easier verification.
 */
export class SignalWrapper {
  // Exposed as an escape hatch
  // Please prefer to use funcs provided by this wrapper instead
  // As this could create UB
  public HubConnection: HubConnection | null;

  private HubBuilder: HubConnectionBuilder;

  private _lock: Option<() => void>;
  private _shouldReconnect: boolean;

  private _callbackStatus: Array<StatusCallback>;
  private _callbackErrors: Array<ErrCallback>;
  private _onStartup: Array<() => void>;
  private _onHooks: Array<onHookStorage>;

  constructor(hubBuilder: HubConnectionBuilder) {
    this.HubBuilder = hubBuilder;
    this.HubConnection = null;
    this._lock = None;
    this._callbackStatus = [];
    this._callbackErrors = [];
    this._shouldReconnect = false;
    this._onStartup = [];
    this._onHooks = [];
  }

  public async Start(userid: number, auth: Auth): Promise<void> {
    // TODO: Move out, as this stops us from making this wrapper usable else where
    const negotiate = await fetch(`${SIG_FLOW_ENDPOINT}/${SIG_HUB_NAME}/negotiate`, {
      method: "GET",
      headers: {
        "x-ms-signalr-userid": userid.toString(),
        Authorization: `Bearer ${auth.token}`,
      },
    });

    if (!negotiate.ok) {
      console.log("Negotiate failed: ", negotiate);

      this.onError(Some(ToErrorSignalR(negotiate.status, CommonSignalRErrors.Negotiate)));
      return;
    }

    const json: NegotiateJson = await negotiate.json();
    this.HubBuilder.withUrl(json.url, {
      accessTokenFactory: () => {
        return json.accessToken;
      },
    });

    const con = this.HubBuilder.build();

    con.onreconnected((connectionId?: string) => {
      this.onStateChangeStr(connectionId);
    });
    con.onreconnecting((error?: Error) => {
      this.onStateChangeErr(error);
    });

    // Not needed as we have our own 'Stop' func
    // the following onclose will throw errors, as it executes a few frames later
    // where the class/func could be destroyed by then
    con.onclose(() => {
      this.onStateChangeErr(undefined);
    });

    await con
      .start()
      .then(() => {
        this.HubConnection = con;
        this.onStateChange();
      })
      .catch((e: Error) => {
        console.error(e);
        this.onError(Some(ToErrorSignalR(0, e.message)));
      });
  }

  // Wrapper around signalR .state
  public State(): HubConnectionState {
    return this.HubConnection ? this.HubConnection.state : HubConnectionState.Disconnected;
  }

  public async Stop(): Promise<void> {
    if (!this.HubConnection) {
      return;
    }

    this._onHooks.forEach((hook) => {
      info("SigR Off Registered", hook.connection, hook.callback, hook.registeredCallback);
      this.HubConnection?.off(hook.connection, hook.registeredCallback);
    });

    await this.HubConnection.stop()
      .catch((e: Error) => {
        this.onError(Some(ToErrorSignalR(0, e.message)));
      })
      .finally(() => {
        this.HubConnection = null;
        this.onStateChange();
      });
  }

  public async Dispose(): Promise<void> {
    await this.Stop();
    this.HubConnection = null;
    this.FreeLock();

    this._lock = None;
    this._callbackStatus = [];
    this._callbackErrors = [];
    this._shouldReconnect = false;
    this._onStartup = [];
    this._onHooks = [];
  }

  public withStatusCallback(func: StatusCallback) {
    this._callbackStatus.push(func);

    return this;
  }

  public withErrorCallback(func: ErrCallback) {
    this._callbackErrors.push(func);

    return this;
  }

  /**
   * Add's this class to the window.SignalRWrapper for easier debugging
   */
  public withDebugSetup() {
    window.SignalRWrapper = this;

    return this;
  }

  /**
   * Ran in order on startup, used in conjunction with 'WithTrueAutoReconnect'
   * SignalR does have auto-reconnect, but its reliability is basically nil
   */
  public withOnStartup(startupCallback: FuncCallback) {
    this._onStartup.push(startupCallback);

    return this;
  }

  /**
   * Remove any matching callbacks from the status and error callback lists
   */
  public removeCallback(func: StatusCallback | ErrCallback | FuncCallback) {
    this._callbackStatus = this._callbackStatus.filter((callback) => callback !== func);

    this._callbackErrors = this._callbackErrors.filter((callback) => callback !== func);

    this._onStartup = this._onStartup.filter((callback) => callback !== func);

    return this;
  }

  /**
   * Starts signalR with auto-reconnect workaround.
   * Internally, signalR does have its own reconnection logic. However, it rarely works correctly.
   *
   * This func is an alternative to Start(userId, auth) where it'll use setTimeout to check that we're still connected
   * If we're disconnected, it'll attempt to reconnect and re-run all startup hooks
   */
  public StartWithAutoConnect(userId: number, auth: Auth, timeoutMs: number) {
    const autoStart = () => {
      if (
        this._shouldReconnect &&
        this.State() !== HubConnectionState.Connected &&
        this.State() !== HubConnectionState.Connecting
      ) {
        if (this.HubConnection) {
          this.Stop().catch((e) => console.error("Failed to stop hub session", e));

          this.HubConnection = null;
        }

        this.Start(userId, auth)
          .then(() => {
            // oxlint-disable-next-line promise/no-callback-in-promise
            this._onStartup.forEach((callback) => callback());
            this._onHooks.forEach((storage) => {
              this.On(storage.connection, storage.callback, true);
            });
          })
          .catch((err) => {
            // oxlint-disable-next-line promise/no-callback-in-promise
            this._callbackErrors.forEach((callback) => callback(this, Some(err)));
            this.HubConnection = null;
          })
          .finally(() => {
            setTimeout(autoStart, timeoutMs);
          });
      } else {
        setTimeout(autoStart, timeoutMs);
      }
    };

    this._shouldReconnect = true;
    autoStart();

    return this;
  }

  // Sets 'window.SignalRWrapper' to this class

  /**
   * Used to attempt to prevent signalR from
   * closing due to browser sleep attempts
   *
   * When signalR is no longer needed, make sure to call 'Stop()' or 'FreeLock()'
   *
   * @remarks
   * https://learn.microsoft.com/en-us/aspnet/core/signalr/javascript-client?view=aspnetcore-9.0&tabs=visual-studio#bsleep
   * https://developer.mozilla.org/en-US/docs/Web/API/Web_Locks_API
   *
   */
  public withLock() {
    if (!navigator || !navigator.locks || typeof navigator.locks.request === "undefined") {
      console.warn(
        "No web lock supported, SignalR may close during inactivity or low power. Please update your browser or switch to latest chromium.",
      );
      return this;
    }

    if (typeof Promise.withResolvers === "undefined") {
      console.warn(
        "No promise withResolvers found, SignalR may close during inactivity or low power. Please update your browser or switch to latest chromium.",
      );
      return this;
    }

    const { promise, resolve } = Promise.withResolvers();

    navigator.locks
      .request(SIG_LOCK_NAME, { mode: "exclusive", ifAvailable: true }, (data) => {
        if (data === null) {
          console.log("Could not get web lock, another tab opened");
          this.onError(
            Some(ToErrorSignalR(InternalSigRErrors.WebLockAlreadyAcquired, "Web lock unavailable")),
          );
          return;
        }

        console.log("🔒 Web lock acquired", data);
        this._lock = Some(resolve as () => void);
        return promise;
      })
      .catch((err) => {
        console.error("Unable to acquire web lock! Expect signalR to close during inactivity", err);
      });

    return this;
  }

  /**
   * Free's the current web lock if one exists
   */
  public FreeLock(): void {
    if (this._lock.isSome()) {
      this._lock.unwrap()();
    }
  }

  /**
   * Registers a callback function to be executed when the SignalR connection status changes.
   *
   * @param {StatusCallback} callback - The callback function to execute when the status changes.
   * @return {() => void} A function that can be invoked to unregister the callback.
   * @remarks Use this if you want a per-component way to access signalR state changes,
   * use withStatus if you need a consitent callback regardless of where a component is loaded
   */
  public OnStatusCallback(callback: StatusCallback): () => void {
    this._callbackStatus.push(callback);

    return () => {
      this.removeCallback(callback);
    };
  }

  /**
   * Registers a callback function to be executed when the SignalR connection hit's an error.
   *
   * @param {ErrCallback} callback - The callback function to execute when an error occours.
   * @return {() => void} A function that can be invoked to unregister the callback.
   * @remarks Use this if you want a per-component way to access signalR error alert,
   * use withError if you need a consistent callback regardless of where a component is loaded
   */
  public OnErrorCallback(callback: ErrCallback): () => void {
    this._callbackErrors.push(callback);

    return () => {
      this.removeCallback(callback);
    };
  }

  /**
   * Registers a callback function to be executed when a specific event or message is received.
   *
   * @param {SigROn | string} connection - The name of the event or message to listen for. This can be either a string or a SigROn type.
   * @param {EscapeHackCallback} callback - The callback function to execute when the event is triggered.
   * @param {boolean} [skipHookStorage=false] - If true, the callback will not be stored for internal use later on
   * @return {Option<() => void>} An Option containing a function that can be invoked to unregister the callback, or None if registration fails.
   */
  public On(
    connection: SigROn | string,
    callback: EscapeHackCallback,
    skipHookStorage?: boolean,
  ): Option<() => void> {
    if (this.HubConnection === null) {
      warn("SigR On called too early", connection);
      return None;
    }

    if (this.HubConnection.state !== HubConnectionState.Connected) {
      warn("SigR On called on invalid state", this.HubConnection.state, connection);
      return None;
    }

    info("SigR On Registered", connection, callback);

    const registeredCallback: EscapeHackCallback = !PRODUCTION
      ? (...args) => {
          console.info("SigR On Callback", connection, args);
          callback(...args);
        }
      : callback;

    if (!skipHookStorage) {
      this._onHooks.push({ connection, callback, registeredCallback });
    }

    this.HubConnection.on(connection, registeredCallback);

    // @ts-expect-error
    metrics.gauge("SigR-On", this.HubConnection._methods.length);

    return Some(() => {
      info("SigR Off Registered", connection, callback, registeredCallback);
      this.HubConnection?.off(connection, registeredCallback);
      this._onHooks = this._onHooks.filter(
        (storage) =>
          !(
            storage.connection === connection &&
            storage.callback === callback &&
            storage.registeredCallback === registeredCallback
          ),
      );
    });
  }

  /**
   * Invokes a method on the SignalR Hub connection with the specified parameters.
   *
   * @param {SigRInvoke} connection - The method or action to invoke on the SignalR Hub.
   * @param {...any[]} args - The arguments to pass to the invoked method.
   * @return {Promise<void>} A promise that resolves when the method is successfully invoked or rejects if an error occurs.
   */
  public async Invoke(connection: SigRInvoke, ...args: any[]): Promise<void> {
    if (this.HubConnection === null) {
      warn("SigR Invoke called too early", connection, ...args);
      return Promise.reject(ToErrorSignalR(0, "SigR invoke called too early"));
    }

    if (this.HubConnection.state !== HubConnectionState.Connected) {
      warn("SigR Invoke called on invalid state", this.HubConnection.state, connection, ...args);
      return Promise.reject(ToErrorSignalR(0, "SigR invoke called on invalid state"));
    }

    info("SigR Invoke", connection, ...args);

    return this.HubConnection.invoke(connection, ...args);
  }

  private onStateChange() {
    if (!this) return;

    const state = this.State();

    warn("SignalR state changed", state, "Firing off callbacks", this._callbackStatus);

    this._callbackStatus.forEach((callback) => {
      callback(this, state, None, None);
    });
  }

  private onStateChangeStr(connectionId: string | undefined) {
    if (!this) return;

    const con = connectionId ? Some(connectionId) : None;
    const state = this.State();

    warn("SignalR state changed", state, "Firing off callbacks", this._callbackStatus);

    this._callbackStatus.forEach((callback) => {
      callback(this, state, con, None);
    });
  }

  private onStateChangeErr(error: Error | undefined) {
    if (!this) return;

    const err = error ? Some(error) : None;
    const state = this.State();

    warn("SignalR state changed", state, "Firing off callbacks", this._callbackStatus);

    this._callbackStatus.forEach((callback) => {
      callback(this, state, None, err);
    });
  }

  private onError(error: Option<ErrorSignalR>) {
    warn("SignalR error hit", error, "Firing off callbacks", this._callbackErrors);

    this._callbackErrors.forEach((callback) => {
      callback(this, error);
    });

    return this;
  }
}

export const useSignalROn = (connection: SigROn | string, callback: EscapeHackCallback): void => {
  const { signalr } = useSignalStore();

  useEffect(() => {
    if (!signalr) {
      return;
    }

    const remove = signalr.On(connection, callback);

    return () => {
      remove.unwrapOr(() => {})();
    };
  }, [callback, connection, signalr]);
};
